import { skipToken } from '@reduxjs/toolkit/query';
import { baseRestApi, getRestFailureUserMessage } from '@src/utils/rtkQuery';
import { CareOrder, NotificationVariants, type SizeModeEnum, type ToastProps, type useToasts } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

interface WorkingOrderPreviewRequestBody {
  ClOrdId: string;
  MemberCareOrders: string[];
  SizeMode: SizeModeEnum;
  SubAccount?: string;
}

interface WorkingOrderModificationPreviewRequestBody extends WorkingOrderPreviewRequestBody {
  OrderID: string;
}

export const CareOrderNettingPreviewMode = {
  Create: 'create',
  Modify: 'modify',
  Approval: 'approval',
  View: 'view',
} as const;
export type CareOrderNettingPreviewMode =
  (typeof CareOrderNettingPreviewMode)[keyof typeof CareOrderNettingPreviewMode];

export const isReadOnlyCareOrderNettingPreviewMode = (mode: CareOrderNettingPreviewMode) =>
  mode === CareOrderNettingPreviewMode.Approval || mode === CareOrderNettingPreviewMode.View;

export const isExistingCareOrderNettingPreviewMode = (mode: CareOrderNettingPreviewMode) =>
  mode === CareOrderNettingPreviewMode.Modify || isReadOnlyCareOrderNettingPreviewMode(mode);

const workingOrderPreviewEndpoints = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    previewWorkingOrder: builder.query<CareOrder[], WorkingOrderPreviewRequestBody>({
      query: body => ({
        method: 'POST',
        url: '/care/working/orders/preview',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      transformResponse: (response: CareOrder[]) => response.map(entry => new CareOrder(entry)),
      keepUnusedDataFor: 0,
    }),
    previewWorkingOrderModification: builder.query<CareOrder[], WorkingOrderModificationPreviewRequestBody>({
      query: body => ({
        method: 'PATCH',
        url: '/care/working/orders/preview',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      transformResponse: (response: CareOrder[]) => response.map(entry => new CareOrder(entry)),
      keepUnusedDataFor: 0,
    }),
  }),
});

const { usePreviewWorkingOrderQuery, usePreviewWorkingOrderModificationQuery } = workingOrderPreviewEndpoints;

type AddToast = ReturnType<typeof useToasts<ToastProps>>['add'];

const getCreateRequestParams = (request: WorkingOrderPreviewRequestBody) => ({
  MemberCareOrders: request.MemberCareOrders,
  SizeMode: request.SizeMode,
  SubAccount: request.SubAccount,
});

const getModifyRequestParams = (request: WorkingOrderModificationPreviewRequestBody) => ({
  OrderID: request.OrderID,
  MemberCareOrders: request.MemberCareOrders,
  SizeMode: request.SizeMode,
  SubAccount: request.SubAccount,
});

/**
 * Fetches a netting preview and exposes the resulting working order state.
 *
 * The API returns preview care orders; this hook surfaces the first valid order as
 * the netted working order, plus loading/error state for the dialog.
 *
 * @param mode Netting preview mode (`create`, `modify`, `approval`, or `view`).
 * @param careOrderIDs Selected care order IDs used as netting inputs.
 * @param orderID Existing netted order ID to modify when in modify mode.
 * @param existingWorkingOrder Existing working order to show in read-only modes.
 * @param sizeMode Size mode to apply to the preview working order.
 * @param subAccount Resolved sub account name to apply to the preview working order.
 * @param addToast Optional toast dispatcher used to surface preview errors.
 */
export function useWorkingOrderPreview({
  mode = CareOrderNettingPreviewMode.Create,
  careOrderIDs,
  orderID,
  existingWorkingOrder,
  sizeMode,
  subAccount,
  addToast,
}: {
  mode?: CareOrderNettingPreviewMode;
  careOrderIDs: string[];
  orderID?: string;
  existingWorkingOrder?: CareOrder;
  sizeMode: SizeModeEnum;
  subAccount?: string;
  addToast?: AddToast;
}) {
  const hasEnoughCareOrdersToPreview = careOrderIDs.length >= 1;
  const canPreviewCreation = mode === CareOrderNettingPreviewMode.Create && hasEnoughCareOrdersToPreview;
  const canPreviewModification =
    mode === CareOrderNettingPreviewMode.Modify && hasEnoughCareOrdersToPreview && orderID != null;

  const [createRequest, setCreateRequest] = useState<WorkingOrderPreviewRequestBody | typeof skipToken>(skipToken);
  const [modifyRequest, setModifyRequest] = useState<WorkingOrderModificationPreviewRequestBody | typeof skipToken>(
    skipToken
  );

  useEffect(() => {
    if (!canPreviewCreation) {
      setCreateRequest(previousRequest => (previousRequest === skipToken ? previousRequest : skipToken));
      return;
    }

    const nextRequestParams = {
      MemberCareOrders: careOrderIDs,
      SizeMode: sizeMode,
      SubAccount: subAccount,
    };

    setCreateRequest(previousRequest => {
      if (previousRequest !== skipToken && isEqual(getCreateRequestParams(previousRequest), nextRequestParams)) {
        return previousRequest;
      }

      return {
        ClOrdId: uuid(),
        ...nextRequestParams,
      };
    });
  }, [canPreviewCreation, careOrderIDs, sizeMode, subAccount]);

  useEffect(() => {
    if (!canPreviewModification || orderID == null) {
      setModifyRequest(previousRequest => (previousRequest === skipToken ? previousRequest : skipToken));
      return;
    }

    const nextRequestParams = {
      OrderID: orderID,
      MemberCareOrders: careOrderIDs,
      SizeMode: sizeMode,
      SubAccount: subAccount,
    };

    setModifyRequest(previousRequest => {
      if (previousRequest !== skipToken && isEqual(getModifyRequestParams(previousRequest), nextRequestParams)) {
        return previousRequest;
      }

      return {
        ClOrdId: uuid(),
        ...nextRequestParams,
      };
    });
  }, [canPreviewModification, careOrderIDs, orderID, sizeMode, subAccount]);

  // Normalize backend errors into a user-friendly message for UI and toast usage.
  const createQuery = usePreviewWorkingOrderQuery(createRequest);
  const modifyQuery = usePreviewWorkingOrderModificationQuery(modifyRequest);
  const { data, isLoading, isFetching, isError, error } =
    mode === CareOrderNettingPreviewMode.Modify ? modifyQuery : createQuery;
  const previewError = isError ? getRestFailureUserMessage(error) : undefined;

  // Show toast once a preview request fails and we have a meaningful error message.
  useEffect(() => {
    if (!isError || addToast == null) {
      return;
    }

    if (previewError == null) {
      return;
    }

    const previewOperationLabel = mode === CareOrderNettingPreviewMode.Modify ? 'netting modification' : 'netted order';
    addToast({
      text: `Could not preview ${previewOperationLabel}: ${previewError}`,
      variant: NotificationVariants.Negative,
    });
  }, [isError, previewError, addToast, mode]);

  if (isReadOnlyCareOrderNettingPreviewMode(mode)) {
    // Approval and View show the persisted WorkingOrder exactly as-is; changing members would create a new preview.
    return {
      workingOrder: existingWorkingOrder,
      loading: false,
      previewError: undefined,
    };
  }

  if (!canPreviewCreation && !canPreviewModification) {
    return {
      workingOrder: undefined,
      loading: false,
      previewError: undefined,
    };
  }

  const loading = isLoading || isFetching;
  // Preserve error details for callers when preview fetch fails.
  if (isError) {
    return {
      workingOrder: undefined,
      loading,
      previewError,
    };
  }

  // Only expose preview order when backend returned a persisted working order ID.
  const workingOrder = data?.at(0);
  if (workingOrder && workingOrder.OrderID != null) {
    return { workingOrder, loading, previewError: undefined };
  }

  return {
    workingOrder: undefined,
    loading,
    previewError: undefined,
  };
}
